// Live expiry status for library documents. Expiry drives AI retrieval:
// expired documents are hard-excluded and won't be cited in drafts, so the
// list surfaces the state as a pill. `today` is injected (never Date.now())
// so callers stay deterministic and testable.

const MS_PER_DAY = 86_400_000

/**
 * The content classes whose expiry date is a validity date — the same list as
 * the backend's `EXPIRING_CONTENT_CLASSES` (`backend/src/lib/evidence-expiry.ts`),
 * which is what AI search excludes. On any other class the date is not an
 * expiry: a case study's project end date was once stored as one (2026-10-12),
 * so a date alone must never raise a pill.
 */
export const EXPIRING_CONTENT_CLASSES = ['insurance_certificate', 'accreditation'] as const

function carriesExpiry(contentClass: string | null | undefined): boolean {
  return contentClass != null && (EXPIRING_CONTENT_CLASSES as readonly string[]).includes(contentClass)
}

/**
 * Parse a date string (either 'YYYY-MM-DD' or a full ISO timestamp) into a
 * UTC-midnight epoch for the calendar day it names. Returns null when the
 * leading date portion isn't a valid Y-M-D.
 */
function parseDateOnly(value: string): number | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value)
  if (!match) return null
  const [, y, m, d] = match
  const year = Number(y)
  const month = Number(m)
  const day = Number(d)
  const utc = Date.UTC(year, month - 1, day)
  // Guard against overflow-normalised dates (e.g. month 13 → next year).
  const check = new Date(utc)
  if (
    check.getUTCFullYear() !== year ||
    check.getUTCMonth() !== month - 1 ||
    check.getUTCDate() !== day
  ) {
    return null
  }
  return utc
}

/**
 * Classify a document's expiry relative to `today` (30-day soon-window):
 * - `'expired'`       — expiry date is before today
 * - `'expiring-soon'` — expiry is today through 30 days out (inclusive)
 * - `null`            — not a certificate class, no expiry date, unparseable,
 *                       or more than 30 days out
 */
export function getExpiryStatus(
  doc: { expiryDate?: string | null | undefined; contentClass?: string | null | undefined },
  today: Date
): 'expired' | 'expiring-soon' | null {
  if (!carriesExpiry(doc.contentClass)) return null
  if (!doc.expiryDate) return null
  const expiry = parseDateOnly(doc.expiryDate)
  if (expiry === null) return null

  const todayOnly = Date.UTC(
    today.getUTCFullYear(),
    today.getUTCMonth(),
    today.getUTCDate()
  )
  const daysUntil = Math.floor((expiry - todayOnly) / MS_PER_DAY)

  if (daysUntil < 0) return 'expired'
  if (daysUntil <= 30) return 'expiring-soon'
  return null
}
